import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import Button from '@mui/material/Button'
import MobileStepper from '@mui/material/MobileStepper'
import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'

import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'

import WaitingButton from 'cityads-v3/components/Buttons/WaitingButton'
import { RootState } from 'cityads-v3/types/reducer'

import { FORM_STEP_COUNT, MIN_FORM_STEP, MAX_FORM_STEP, useAdvertisingMarkingForm } from './AdvertisingMarkingForm.hooks'

import Step1 from './Step1'
import Step2 from './Step2'
import Step3 from './Step3'

import classes from './AdvertisingMarkingForm.scss'

export default function AdvertisingMarkingForm(): JSX.Element {
  const { t } = useTranslation(['Publisher'])

  const {
    formFields,
    activeStep,
    goBack,
    goNext,
    isSaving,
    countries,
    countryRegion,
  } = useAdvertisingMarkingForm()

  const isMobile = useSelector((state: RootState) => state.app.isMobile)

  return (
    <>
      {isMobile ?
        (
          <MobileStepper
            className  = {classes.mobileStepper}
            steps      = {FORM_STEP_COUNT}
            position   = "static"
            variant    = "dots"
            activeStep = {activeStep}
            backButton = {(
              <Button size="small" onClick={goBack} disabled={activeStep === MIN_FORM_STEP}>
                {t('main:buttons.back')}
                <KeyboardArrowLeft />
              </Button>
            )}
            nextButton = {(
              <WaitingButton size="small" onClick={goNext} waiting={isSaving}>
                {(activeStep < MAX_FORM_STEP) ? t('main:buttons.next') : t('main:buttons.finish')}
                <KeyboardArrowRight />
              </WaitingButton>
            )}
          />
        )
        :
        (
          <Stepper activeStep={activeStep} className={classes.stepper}>
            <Step><StepLabel>{t('advertisingMarking.form.tab1')}</StepLabel></Step>
            <Step><StepLabel>{t('advertisingMarking.form.tab2')}</StepLabel></Step>
            <Step><StepLabel>{t('advertisingMarking.form.tab3')}</StepLabel></Step>
          </Stepper>
        )
      }

      {(activeStep === 0) && (
        <Step1
          countries     = {countries}
          country       = {formFields.country}
          countryRegion = {countryRegion}
        />
      )}

      {(activeStep === 1) && (
        <Step2
          lastName     = {formFields.lastName}
          firstName    = {formFields.firstName}
          middleName   = {formFields.middleName}
          legalSubject = {formFields.legalSubject}
        />
      )}

      {(activeStep === 2) && (
        <Step3
          legalSubject    = {formFields.legalSubject}
          legalName       = {formFields.legalName}
          vatNumber       = {formFields.vatNumber}
          psrn            = {formFields.psrn}
          legalAddress    = {formFields.legalAddress}
          email           = {formFields.email}
          websiteUrl      = {formFields.websiteUrl}
          ownershipRights = {formFields.ownershipRights}
        />
      )}

      <div className={classes.footerButtons}>
        <Button onClick={goBack} disabled={activeStep === MIN_FORM_STEP}>
          {t('main:buttons.back')}
        </Button>

        <WaitingButton variant="contained" color="primary" onClick={goNext} waiting={isSaving}>
          {(activeStep < MAX_FORM_STEP) ? t('main:buttons.next') : t('main:buttons.finish')}
        </WaitingButton>
      </div>
    </>
  )
}
